import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { nullToUndefined } from '../../lib/utils'
import { LeadForm } from '../../components/forms/LeadForm'
import { Avatar } from '../../components/ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import { useFls } from '../../hooks/useFls'
import { RecordSharePanel } from '../../components/sharing/RecordSharePanel'
import { AuditHistoryPanel } from '../../components/audit/AuditHistoryPanel'
import { EmailDropZone, EmailList, EmailDetail } from '../../components/emails'
import type { Lead, LeadInput } from '../../../shared/types'

const leadsApi = createCrudApi<Lead, LeadInput>('leads')

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)
  const { t } = useTranslation()
  const { getUser } = useUserLookup()
  const { canRead, canWrite } = useFls('leads')

  const { data: lead, isLoading } = useQuery({
    queryKey: ['leads', id],
    queryFn: () => leadsApi.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<LeadInput>) => leadsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', id] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => leadsApi.delete(id!),
    onSuccess: () => {
      navigate('/leads')
    },
  })

  const convertMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/leads/${id}/convert`, {
        method: 'POST',
      })
      if (!response.ok) throw new Error('Conversion failed')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', id] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })

  if (isLoading) return <div>{t('common.loading')}</div>
  if (!lead) return <div>{t('pages.leads.notFound')}</div>

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/leads"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.leads.backToList')}
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-x-4">
            <Avatar firstName={lead.lastName ? lead.firstName : null} lastName={lead.lastName || lead.company} size="xl" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {lead.lastName ? `${lead.firstName || ''} ${lead.lastName}`.trim() : lead.company}
              </h1>
              <p className="mt-1 text-sm text-gray-500">{lead.company}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          {!lead.converted && (
            <button
              onClick={() => convertMutation.mutate()}
              disabled={convertMutation.isPending}
              className="w-full inline-flex justify-center items-center gap-x-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              {t('pages.leads.convertToAccount')}
            </button>
          )}
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.leads.confirmDelete'))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.status')}</dt>
              <dd className="mt-1">
                <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-brand-100 text-brand-800">
                  {lead.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.email')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{lead.email || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.phone')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{lead.phone || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.rating')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{lead.rating || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.source')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{lead.source || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.owner')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {(() => {
                  const owner = getUser(lead.ownerId)
                  if (!owner) return '-'
                  return (
                    <div className="flex items-center gap-x-2">
                      <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                      <span>{owner.firstName} {owner.lastName}</span>
                    </div>
                  )
                })()}
              </dd>
            </div>
            {lead.converted && (
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('fields.converted')}</dt>
                <dd className="mt-1 text-sm text-brand-600 font-medium">
                  {new Date(lead.convertedAt!).toLocaleDateString()}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Record Sharing */}
        <RecordSharePanel entity="leads" entityId={id!} ownerId={lead.ownerId ?? undefined} />

        {/* Audit History */}
        <AuditHistoryPanel entity="leads" entityId={id!} />
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
        {/* Mobile header */}
        <div className="xl:hidden mb-6">
          <div className="mb-4">
            <Link
              to="/leads"
              className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              {t('pages.leads.backToList')}
            </Link>
          </div>
          <div className="flex gap-x-3">
            {!lead.converted && (
              <button
                onClick={() => convertMutation.mutate()}
                disabled={convertMutation.isPending}
                className="inline-flex items-center gap-x-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
              >
                {t('pages.leads.convertToAccount')}
              </button>
            )}
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="inline-flex items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              <PencilIcon className="h-4 w-4" />
              {isEditing ? t('common.cancel') : t('common.edit')}
            </button>
            <button
              onClick={() => {
                if (confirm(t('pages.leads.confirmDelete'))) {
                  deleteMutation.mutate()
                }
              }}
              className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              <TrashIcon className="h-4 w-4" />
              {t('common.delete')}
            </button>
          </div>
        </div>

        {/* Detail card */}
        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-6 sm:px-6">
            <div className="flex items-center gap-x-4">
              <Avatar firstName={lead.lastName ? lead.firstName : null} lastName={lead.lastName || lead.company} size="lg" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {lead.lastName ? `${lead.firstName || ''} ${lead.lastName}`.trim() : lead.company}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('pages.leads.detailSubtitle')}</p>
              </div>
            </div>
          </div>

          {isEditing ? (
            <div className="border-t border-gray-100 px-4 py-6 sm:px-6">
              <LeadForm
                defaultValues={nullToUndefined(lead) as Partial<LeadInput>}
                onSubmit={(data) => updateMutation.mutate(data)}
                onCancel={() => setIsEditing(false)}
                loading={updateMutation.isPending}
                canWriteField={canWrite}
              />
            </div>
          ) : (
            <div className="border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.fullName')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {lead.lastName ? `${lead.firstName || ''} ${lead.lastName}`.trim() : lead.company}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.organisation')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {lead.company}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.email')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {lead.email || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.phone')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {lead.phone || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.status')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-brand-100 text-brand-800">
                      {lead.status}
                    </span>
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.rating')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {lead.rating || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.source')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {lead.source || '-'}
                  </dd>
                </div>
                {lead.converted && (
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('fields.converted')}</dt>
                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                      <span className="text-brand-600 font-medium">✓ {new Date(lead.convertedAt!).toLocaleDateString()}</span>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        {/* Company Details Section */}
        {(lead.street || lead.city || lead.website || lead.description || lead.managingDirector || lead.hrbNumber) && (
          <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Company Details</h3>
            </div>
            <dl className="divide-y divide-gray-100">
              {(lead.street || lead.city || lead.postalCode || lead.country) && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">Address</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {lead.street && <div>{lead.street}</div>}
                    <div>{[lead.postalCode, lead.city].filter(Boolean).join(' ')}</div>
                    {lead.country && <div>{lead.country}</div>}
                  </dd>
                </div>
              )}
              {lead.website && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.website')}</dt>
                  <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                    <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-900">
                      {lead.website}
                    </a>
                  </dd>
                </div>
              )}
              {lead.description && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.description')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{lead.description}</dd>
                </div>
              )}
              {lead.managingDirector && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">Managing Director</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{lead.managingDirector}</dd>
                </div>
              )}
              {lead.hrbNumber && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">HRB Number</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{lead.hrbNumber}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* B2B Intelligence Section */}
        {(lead.b2bScore != null || lead.tier || lead.category || lead.salesPlay || lead.contactQuality || lead.digitalSignals || lead.techStack) && (
          <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">B2B Intelligence</h3>
            </div>
            <dl className="divide-y divide-gray-100">
              {lead.b2bScore != null && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">B2B Score</dt>
                  <dd className="mt-1 sm:col-span-2 sm:mt-0">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                        <div
                          className={`h-2 rounded-full ${
                            lead.b2bScore >= 70 ? 'bg-green-500' :
                            lead.b2bScore >= 40 ? 'bg-amber-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${lead.b2bScore}%` }}
                        />
                      </div>
                      <span className={`text-sm font-semibold ${
                        lead.b2bScore >= 70 ? 'text-green-700' :
                        lead.b2bScore >= 40 ? 'text-amber-700' :
                        'text-red-700'
                      }`}>
                        {lead.b2bScore}
                      </span>
                    </div>
                  </dd>
                </div>
              )}
              {lead.tier && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">Tier</dt>
                  <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                    <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                      lead.tier === 'Tier 1' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {lead.tier}
                    </span>
                  </dd>
                </div>
              )}
              {lead.category && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.category')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{lead.category}</dd>
                </div>
              )}
              {lead.salesPlay && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">Sales Play</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{lead.salesPlay}</dd>
                </div>
              )}
              {lead.contactQuality && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">Contact Quality</dt>
                  <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                    <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                      lead.contactQuality === 'High' ? 'bg-green-100 text-green-800' :
                      lead.contactQuality === 'Medium' ? 'bg-amber-100 text-amber-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {lead.contactQuality}
                    </span>
                  </dd>
                </div>
              )}
              {lead.digitalSignals && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">Digital Signals</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{lead.digitalSignals}</dd>
                </div>
              )}
              {lead.techStack && (
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">Tech Stack</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">{lead.techStack}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* Team Members Section */}
        {lead.teamMembers && lead.teamMembers.length > 0 && (
          <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Team Members</h3>
            </div>
            <div className="px-4 py-4 sm:px-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="py-2 pr-3 text-left text-xs font-medium text-gray-500">{t('fields.name')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('fields.title')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('fields.category')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lead.teamMembers
                    .filter((m) => !m.title || m.title.length < 200)
                    .map((member, idx) => (
                    <tr key={idx}>
                      <td className="py-2 pr-3 text-sm text-gray-900">{member.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{member.title || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{member.category || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Emails Section */}
        <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">{t('nav.emails') || 'Emails'}</h3>
            <p className="mt-1 text-sm text-gray-500">Emails linked to this lead</p>
          </div>

          <div className="px-4 py-4 sm:px-6">
            <EmailDropZone
              targetType="lead"
              targetId={id}
              compact
            />
          </div>

          <div className="px-4 pb-4 sm:px-6">
            <EmailList
              leadId={id}
              onEmailClick={setSelectedEmailId}
              limit={5}
            />
          </div>
        </div>
        </div>
      </div>

      {/* Email Detail Modal */}
      {selectedEmailId && (
        <EmailDetail
          emailId={selectedEmailId}
          onClose={() => setSelectedEmailId(null)}
        />
      )}
    </>
  )
}
